import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import AppShell from "@/components/AppShell";
import { LogoMark } from "@/components/Logo";
import Onboarding from "@/pages/Onboarding";
import AuthPage from "@/pages/AuthPage";
import Home from "@/pages/Home";
import NewMemory from "@/pages/NewMemory";
import Memories from "@/pages/Memories";
import MemoryDetail from "@/pages/MemoryDetail";
import Ask from "@/pages/Ask";
import Reminders from "@/pages/Reminders";
import Profile from "@/pages/Profile";
import Paywall from "@/pages/Paywall";
import Admin from "@/pages/Admin";
import Privacy from "@/pages/Privacy";
import Categories from "@/pages/Categories";
import { TermsOfUse, PrivacyPolicy } from "@/pages/Legal";
import Help from "@/pages/Help";
import NotificationSettings from "@/pages/NotificationSettings";
import Security from "@/pages/Security";
import VerifyEmail from "@/pages/VerifyEmail";
import TwoFactor from "@/pages/TwoFactor";
import Vault from "@/pages/Vault";
import Chat from "@/pages/Chat";
import ChatConversation from "@/pages/ChatConversation";
import ChatNew from "@/pages/ChatNew";
import ChatSettings from "@/pages/ChatSettings";
import { registerSW } from "@/lib/push";

function Splash() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center text-white" style={{ background: "linear-gradient(180deg,#05324A,#0A6373)" }} data-testid="splash-screen">
      <LogoMark size={96} />
      <p className="mt-6 text-sm text-white/70">Você vive, a gente lembra.</p>
    </div>
  );
}

function Protected() {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to={localStorage.getItem("ga_onboarded") ? "/entrar" : "/bem-vindo"} replace />;
  return <Outlet />;
}

function Public({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (user) return <Navigate to="/inicio" replace />;
  return children;
}

function ResetPassword() {
  const { user, loading, logout } = useAuth();
  const hasToken = new URLSearchParams(window.location.search).has("token");
  useEffect(() => { if (hasToken && user) logout(); }, [hasToken, user, logout]);
  if (!hasToken) return <Public><AuthPage mode="forgot" /></Public>;
  if (loading || user) return <Splash />;
  return <AuthPage mode="reset" />;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  return user?.role === "admin" ? children : <Navigate to="/perfil" replace />;
}

export default function App() {
  useEffect(() => {
    registerSW().catch(() => {});
  }, []);
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/bem-vindo" element={<Public><Onboarding /></Public>} />
            <Route path="/entrar" element={<Public><AuthPage mode="login" /></Public>} />
            <Route path="/verificacao-2fa" element={<Public><TwoFactor /></Public>} />
            <Route path="/cadastro" element={<Public><AuthPage mode="register" /></Public>} />
            <Route path="/recuperar-senha" element={<ResetPassword />} />
            <Route path="/termos" element={<TermsOfUse />} />
            <Route path="/politica-de-privacidade" element={<PrivacyPolicy />} />
            <Route element={<Protected />}>
              <Route element={<AppShell />}>
                <Route path="/inicio" element={<Home />} />
                <Route path="/memorias" element={<Memories />} />
                <Route path="/memorias/:id" element={<MemoryDetail />} />
                <Route path="/nova/:mode" element={<NewMemory />} />
                <Route path="/perguntar" element={<Ask />} />
                <Route path="/lembretes" element={<Reminders />} />
                <Route path="/perfil" element={<Profile />} />
                <Route path="/ajuda" element={<Help />} />
                <Route path="/planos" element={<Paywall />} />
                <Route path="/privacidade" element={<Privacy />} />
                <Route path="/categorias" element={<Categories />} />
                <Route path="/notificacoes" element={<NotificationSettings />} />
                <Route path="/seguranca" element={<Security />} />
                <Route path="/verificar-email" element={<VerifyEmail />} />
                <Route path="/pasta-segura" element={<Vault />} />
                <Route path="/chat" element={<Chat />} />
                <Route path="/chat/novo" element={<ChatNew />} />
                <Route path="/chat/ajustes" element={<ChatSettings />} />
                <Route path="/chat/c/:peerId" element={<ChatConversation />} />
                <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/inicio" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors closeButton toastOptions={{ style: { fontFamily: "Poppins, sans-serif", borderRadius: 16 } }} />
      </AuthProvider>
    </ThemeProvider>
  );
}
